// FLAG:docuAiEnhancements — the tenant's OWN tax categories for the receipt chat's
// SITUATION block (DMS issue 2ed289cb).
//
// WHY this exists, and why it is NOT a knowledge-base change: the RAG knowledge base
// (dms-s3 rag_service, kb_* indices) is global and has no tenant isolation — kb_groups.py
// knows only a customer/dev scope. Per-tenant master data must therefore never be indexed
// there. The situation block is the channel that already carries tenant truth, and it is
// applied AFTER retrieval (llm-gateway rag-chat.service.ts), so it steers the answer
// without touching the search.
//
// The cost argument: sending a SHORT, already-filtered list costs a few hundred prompt
// tokens once per turn and no extra model call — as opposed to per-tenant indices or an
// LLM round-trip that maps a document to a chart of accounts. Keeping the list short is
// the entire point of this module, so the selection below is deliberately strict.

/** The subset of bookingService's ChartOfAccount this module needs (see
 * `@/modules/banking/services/bookingService` for the full typedef). Everything is
 * optional: the duck holds whatever the API returned, and a partial row must degrade to
 * "skip this line", never to a crash inside a chat render. */
export interface TaxCategoryAccount {
  id?: string
  accountNo?: number | string
  accountDescriptionCustom?: string
  accountDescriptionShort?: string
  accountDescriptionLong?: string
  favorite?: boolean
  vatDefaultRate?: number
  allowedVatRates?: { values?: number[], default?: number }
}

// Hard ceiling, mirrored by @ArrayMaxSize(40) on LlmRagChatSituationDto.taxCategories in
// dms-s3. A tenant with more favourites than this has an unusual setup; truncating is the
// right call because the list exists to be cheap, and the gateway rule tells the model the
// list is a selection rather than the complete chart of accounts.
export const MAX_TAX_CATEGORIES = 40

/** Custom name first: it is what the tenant renamed the account to and therefore what the
 * reviewer sees in the tax category picker. The SKR long text is the final fallback. */
const descriptionOf = (account: TaxCategoryAccount): string | null => {
  const description = [
    account.accountDescriptionCustom,
    account.accountDescriptionShort,
    account.accountDescriptionLong,
  ].find(value => typeof value === 'string' && value.trim())
  return description ? `${description}`.trim() : null
}

const vatRateOf = (account: TaxCategoryAccount): number | null => {
  const rate = account.allowedVatRates?.default ?? account.vatDefaultRate
  return typeof rate === 'number' && Number.isFinite(rate) ? rate : null
}

/**
 * One account as the single line the model gets: "4930 — Bürobedarf (19 %)".
 *
 * Returns null for a row without a number or without any description — a half-rendered
 * line ("— (19 %)") is worse than a missing one, because the model would quote it back.
 */
export const formatTaxCategory = (account: TaxCategoryAccount | null | undefined): string | null => {
  if (!account || typeof account !== 'object') return null

  const accountNo = account.accountNo
  const hasNumber = typeof accountNo === 'number'
    ? Number.isFinite(accountNo)
    : typeof accountNo === 'string' && !!accountNo.trim()
  const description = descriptionOf(account)
  if (!hasNumber || !description) return null

  const vatRate = vatRateOf(account)
  const suffix = vatRate === null ? '' : ` (${vatRate} %)`
  return `${accountNo} — ${description}${suffix}`.slice(0, 120)
}

/**
 * The tenant's categories, shortened to what is worth sending.
 *
 * Favourites first (the tenant curated them), then the rest in the order the duck holds
 * them — which is the API's `orderBy: accountNo`, i.e. the order of the picker. Deduped by
 * rendered line, so the same account arriving from both `categories` and
 * `favoriteCategories` does not occupy two slots.
 */
export const selectTenantTaxCategories = (
  accounts: TaxCategoryAccount[] | null | undefined,
  limit: number = MAX_TAX_CATEGORIES,
): string[] => {
  if (!Array.isArray(accounts) || !accounts.length) return []

  const favourites: string[] = []
  const others: string[] = []
  // Array.from, never [...set]: omxweb/app's babel runs with loose spread, where spreading a
  // Set silently yields the wrong result.
  const seen = new Set<string>()

  accounts.forEach(account => {
    const line = formatTaxCategory(account)
    if (!line || seen.has(line)) return
    seen.add(line)
    if (account?.favorite) favourites.push(line)
    else others.push(line)
  })

  return favourites.concat(others).slice(0, Math.max(0, limit))
}

/**
 * The category already pre-filled on this document's lines — the supplier default seeded at
 * ingest (receiptReviewCapabilities.applySupplierDefaultTaxCategory) or the last-used
 * fallback. Read-only from the document the drawer already holds; no lookup.
 *
 * Takes the category of the FIRST line that carries one: the prefill writes the same
 * category to every line, and if a reviewer has since split the lines by hand, naming the
 * first is still honest — the gateway wording says it is a prefill, not a verdict.
 */
export const readDefaultTaxCategory = (file): string | null => {
  const positions = file?.documentInvoiceDetails?.positions
  if (!Array.isArray(positions)) return null

  const position = positions.find(entry => entry?.bookkeepingAccountId
    && (entry?.bookkeepingAccountNo || entry?.bookkeepingAccountDescriptionCustom))
  if (!position) return null

  return formatTaxCategory({
    accountNo: position.bookkeepingAccountNo,
    accountDescriptionCustom: position.bookkeepingAccountDescriptionCustom,
    allowedVatRates: position.allowedVatRates,
  })
}
